<template>
  <div class="draggable-group-container">
    <draggable 
      v-model="localSegments" 
      item-key="id"
      handle=".drag-handle"
      animation="200"
      :scroll="true"
      :scroll-sensitivity="150"
      :force-fallback="false"
      :group="dragOptions"
      class="drag-wrapper"
      :class="{ 
        'empty-group': localSegments.length === 0,
        'source-trace-wrapper': isSourceTrace
      }"
      ghost-class="ghost"
      :disabled="isDisabled"
      :clone="clone"
    >
      <template #item="{ element, index }">
      <div 
        class="segment-item" 
        :class="{ 
          'fine-tuning-mode': fineTuning, 
          'readonly-item': readonly,
          'unmatched': isSourceTrace && !element.transcription_id,
          'dup-item': !!getDupInfo(element)
        }"
        :data-segment-id="element.id"
        :style="dupItemStyle(element)"
        :key="element.id" 
      >
        <div class="segment-header" @click="handleSegmentClick(element)">
          <div class="selection-checkbox" @click.stop v-if="!isSourceTrace">
            <input 
              type="checkbox" 
              :checked="selectedIds.includes(element.id)" 
              @change="toggleSelect(element.id)"
            >
          </div>
          <div class="drag-handle" title="拖拽排序" v-if="(!readonly || enableDragOut) && !(isSourceTrace && !element.transcription_id)">
            <i class="mdi mdi-drag-vertical"></i>
          </div>
          <div class="segment-info" v-if="!fineTuning">
            <div class="seg-title" :title="element.text || element.video_name">
              {{ element.text || element.video_name }}
            </div>
            <div class="seg-time" v-if="!isSourceTrace || element.transcription_id">{{ formatTime(element.start_time) }} - {{ formatTime(element.end_time) }}</div>
            <div class="seg-time" v-else>未匹配</div>
          </div>
          <div class="segment-actions">
            <span 
              v-if="getDupInfo(element)" 
              class="dup-badge"
              :style="{ backgroundColor: getDupInfo(element).color }"
              :title="dupTooltip(element)"
              @click.stop="jumpToDup(element)"
            >
              {{ getDupInfo(element).type === 'dup' ? '重' : '叠' }}
            </span>
            <div 
              class="preview-btn-wrapper"
              v-if="isSourceTrace && element.hot_video_url && element.hot_end_time > 0"
              @mouseenter="showVideoPreview(element, $event)" 
              @mouseleave="hideVideoPreview"
            >
              <button class="action-btn hot-video-btn" title="预览热点对应片段">
                <i class="mdi mdi-play-box-outline"></i>
              </button>
            </div>
            <button class="delete-btn" @click.stop="removeSegment(index)" v-if="!readonly">×</button>
          </div>
        </div>
        
        <div v-if="fineTuning" class="fine-tuning-area" @click.self="handleSegmentClick(element)">
          <div v-if="!element.words || element.words.length === 0" 
               :class="(element.text || element.origin_text) ? 'plain-text-content' : 'no-words-hint'" 
               @click.self="handleSegmentClick(element)">
            {{ element.text || element.origin_text || '暂无逐字数据' }}
          </div>
          <div v-else class="words-container" @click.self="handleSegmentClick(element)">
            <WordChunk 
              v-for="(chunk, cIndex) in getWordChunks(element.words)" 
              :key="cIndex"
              :chunk="chunk"
              :segment-index="index"
              :group-index="groupIndex"
              :selected-word-locations-length="selectedWordLocations.length"
              :is-mergeable="isMergeable(index)"
              :selected-word-set="selectedWordSet"
              :playing-video-id="playingVideoId"
              :current-time="currentTime"
              @word-click="forwardWordClick"
              @word-mousedown="forwardWordMouseDown"
              @word-mouseover="forwardWordMouseOver"
              @split-segment="$emit('split-segment', $event)"
              @merge-segment="$emit('merge-segment', $event)"
            />
          </div>
        </div>
      </div>
    </template>
    <template #footer>
       <div v-if="localSegments.length === 0" class="empty-placeholder">
         拖拽片段到此处
       </div>
    </template>
  </draggable>

  <teleport to="body">
    <div 
      v-if="previewElement" 
      class="video-preview-popup" 
      :style="previewStyle"
      @mouseenter="cancelHidePreview"
      @mouseleave="hideVideoPreview"
    >
      <video 
        ref="previewVideo"
        :src="previewVideoUrl"
        class="preview-video"
        @loadedmetadata="onPreviewVideoLoaded"
      ></video>
      <div class="preview-info" v-if="previewElement.text">
        {{ previewElement.text }}
      </div>
    </div>
  </teleport>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import WordChunk from './WordChunk.vue'

export default {
  name: 'DraggableGroup',
  components: { draggable, WordChunk },
  props: {
    segments: {
      type: Array,
      required: true
    },
    groupIndex: {
      type: Number,
      required: true
    },
    fineTuning: {
      type: Boolean,
      default: false
    },
    selectedWordLocations: {
      type: Array,
      default: () => []
    },
    selectedWordLocation: {
      type: Object,
      default: null
    },
    currentTime: {
      type: Number,
      default: 0
    },
    playingVideoId: {
      type: [String, Number],
      default: null
    },
    readonly: {
      type: Boolean,
      default: false
    },
    enableDragOut: {
      type: Boolean,
      default: false
    },
    isSourceTrace: {
      type: Boolean,
      default: false
    },
    clone: {
      type: Function,
      default: (original) => original
    },
    selectedIds: {
      type: Array,
      default: () => []
    },
    // 重复素材标记信息：{ [segmentId]: { color, type: 'dup'|'overlap', count, pairIds, pairs } }
    dupInfo: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['segment-click', 'word-click', 'word-mousedown', 'word-mouseover', 'split-segment', 'merge-segment', 'update:selectedIds', 'dup-jump'],
  data() {
      return {
          previewElement: null,
          previewStyle: { top: '0px', left: '0px' },
          hidePreviewTimer: null
      }
  },
  created() {
      // 使用非响应式属性跟踪高亮，避免触发 Vue 重新渲染
      this._highlightState = null;
  },
  watch: {
    currentTime(newTime) {
      this.updateHighlight(newTime)
    }
  },
  beforeUnmount() {
    this.cancelHidePreview();
    if (window.currentHotAudio) {
      window.currentHotAudio.pause();
      window.currentHotAudio.removeAttribute('src');
      window.currentHotAudio.load();
      window.currentHotAudio = null;
    }
  },
  computed: {
    dragOptions() {
        if (this.enableDragOut) {
            return { name: 'clips', pull: 'clone', put: false, sort: false, filter: '.unmatched' }
        }
        return { name: 'clips' }
    },
    isDisabled() {
        // If readonly is true, we usually disable interactions.
        // But if enableDragOut is true, we must allow dragging (so disabled=false),
        // but the dragOptions will restrict 'put' and 'sort'.
        if (this.readonly && !this.enableDragOut) return true
        return false
    },
    localSegments: {
      get() {
        return this.segments
      },
      set(value) {
        if (this.readonly) return
        this.$store.dispatch('multiClipper/updateGroupSegments', {
          groupIndex: this.groupIndex,
          segments: value
        })
      }
    },
    selectedWordSet() {
      const set = new Set()
      if (!this.selectedWordLocations) return set
      for (let i = 0; i < this.selectedWordLocations.length; i++) {
        const loc = this.selectedWordLocations[i]
        if (loc.groupIndex === this.groupIndex) {
          set.add(`${loc.segmentIndex}-${loc.wordIndex}`)
        }
      }
      return set
    },
    previewVideoUrl() {
      if (!this.previewElement || !this.previewElement.hot_video_url) return '';
      const start = this.previewElement.hot_start_time / 1000;
      const end = this.previewElement.hot_end_time / 1000;
      return `${this.previewElement.hot_video_url}#t=${start},${end}`;
    }
  },
  methods: {
    removeSegment(index) {
      if (this.readonly) return
      this.$store.dispatch('multiClipper/deleteSegment', {
        groupIndex: this.groupIndex,
        segmentIndex: index
      })
    },
    getWordChunks(words) {
      if (!words) return [];
      const CHUNK_SIZE = 150; // 每 150 个字一个 Chunk，兼顾 DOM 数量和 IntersectionObserver 触发频率
      const chunks = [];
      for (let i = 0; i < words.length; i += CHUNK_SIZE) {
          const chunk = words.slice(i, i + CHUNK_SIZE).map((w, localIndex) => {
              // 注入原始索引，因为传入子组件后索引会变成局部的
              return { ...w, _originalIndex: i + localIndex };
          });
          chunks.push(chunk);
      }
      return chunks;
    },
    forwardWordClick(payload) {
        this.$emit('word-click', { ...payload, segment: this.segments[payload.segmentIndex] });
    },
    forwardWordMouseDown(payload) {
        this.$emit('word-mousedown', payload);
    },
    forwardWordMouseOver(payload) {
        this.$emit('word-mouseover', payload);
    },
    handleSegmentClick(segment) {
        if (this.isSourceTrace && !segment.transcription_id) return
        this.$emit('segment-click', segment)
    },
    // 获取片段对应的重复标记信息，无则返回 null
    getDupInfo(seg) {
        if (!seg || !this.dupInfo) return null
        return this.dupInfo[seg.id] || null
    },
    // 重复片段样式：通过 CSS 变量注入重复组颜色
    dupItemStyle(seg) {
        const info = this.getDupInfo(seg)
        if (!info) return {}
        return { '--dup-color': info.color }
    },
    // 重复标记 tooltip：列出同组配对片段
    dupTooltip(seg) {
        const info = this.getDupInfo(seg)
        if (!info) return ''
        const typeText = info.type === 'dup' ? '完全重复' : '部分重叠'
        const lines = [`⚠ 与 ${info.count} 条片段同组（${typeText}），点击定位配对片段`]
        ;(info.pairs || []).forEach(p => {
            lines.push(`${p.text}  [${this.formatTime(p.start)} - ${this.formatTime(p.end)}]`)
        })
        return lines.join('\n')
    },
    // 点击重复标记：定位到同组的配对片段
    jumpToDup(seg) {
        const info = this.getDupInfo(seg)
        if (!info) return
        const targetId = (info.pairIds || []).find(id => id !== seg.id) || (info.pairIds || [])[0]
        if (targetId) this.$emit('dup-jump', targetId)
    },
    toggleSelect(id) {
      const newSelected = [...this.selectedIds]
      const index = newSelected.indexOf(id)
      if (index > -1) {
        newSelected.splice(index, 1)
      } else {
        newSelected.push(id)
      }
      this.$emit('update:selectedIds', newSelected)
    },
    handleWordClick(event, segmentIndex, wordIndex, word) {
        this.$emit('word-click', { event, groupIndex: this.groupIndex, segmentIndex, wordIndex, word, segment: this.segments[segmentIndex] })
    },
    handleWordMouseDown(event, segmentIndex, wordIndex, word) {
        this.$emit('word-mousedown', { event, groupIndex: this.groupIndex, segmentIndex, wordIndex, word })
    },
    handleWordMouseOver(event, segmentIndex, wordIndex, word) {
        this.$emit('word-mouseover', { event, groupIndex: this.groupIndex, segmentIndex, wordIndex, word })
    },
    isWordSelected(segmentIndex, wordIndex) {
        if (!this.selectedWordLocations || this.selectedWordLocations.length === 0) return false
        return this.selectedWordSet.has(`${segmentIndex}-${wordIndex}`)
    },
    isMergeable(index) {
        if (index <= 0) return false;
        const current = this.segments[index];
        const prev = this.segments[index - 1];
        
        const curId = current.video_id || current.transcription_id || current.id;
        const prevId = prev.video_id || prev.transcription_id || prev.id;
        if (curId !== prevId) return false;

        if (!prev.words || prev.words.length === 0) return false;
        if (!current.words || current.words.length === 0) return false;

        const prevLastWord = prev.words[prev.words.length - 1];
        const curFirstWord = current.words[0];

        if (prevLastWord.index_w !== undefined && curFirstWord.index_w !== undefined) {
            return curFirstWord.index_w === prevLastWord.index_w + 1;
        }
        return false;
    },
    updateHighlight(time) {
       if (!this.playingVideoId) {
           this.clearHighlightDOM();
           return;
       }
       
       const timeMs = time * 1000;
       
       let foundSegIndex = -1;
       let foundWordIndex = -1;

       // 0. 优先级 1: 逻辑同步优化 (解决重复片段的高亮偏移)
       // 如果 ClipPool 提供了一个明确的“当前应当高亮位置”，且它在本组内，则优先验证它
       if (this.selectedWordLocation && this.selectedWordLocation.groupIndex === this.groupIndex) {
           const sIdx = this.selectedWordLocation.segmentIndex;
           const seg = this.segments[sIdx];
           if (seg && seg.video_id == this.playingVideoId && seg.words && seg.words.length > 0) {
               // 检查时间是否基本吻合 (允许1秒缓冲)
               if (timeMs >= seg.start_time - 1000 && timeMs <= seg.end_time + 1000) {
                   const wIdx = this.findWordIndexAtTime(seg, timeMs);
                   if (wIdx !== -1) {
                       this.applyHighlightDOM(sIdx, wIdx);
                       return;
                   }
               }
           }
       }

       // 1. O(1) 缓存优化：如果是连续播放，大概率还在当前字或下一个字
       if (this._highlightState) {
           const { segmentIndex, wordIndex } = this._highlightState;
           const seg = this.segments[segmentIndex];
           if (seg && seg.video_id == this.playingVideoId && seg.words) {
               const currentWord = seg.words[wordIndex];
               if (currentWord && timeMs >= currentWord.start_time && timeMs <= currentWord.end_time) {
                   return; // 还在当前字内，无需操作
               }
               const nextWord = seg.words[wordIndex + 1];
               if (nextWord && timeMs >= nextWord.start_time && timeMs <= nextWord.end_time) {
                   this.applyHighlightDOM(segmentIndex, wordIndex + 1);
                   return;
               }
           }
       }

       // 2. O(log M) 搜索优化：二分查找代替线性遍历
       for (let i = 0; i < this.segments.length; i++) {
           const seg = this.segments[i];
           if (seg.video_id != this.playingVideoId) continue;
           
           if (seg.words && seg.words.length > 0) {
               if (timeMs < seg.start_time - 1000 || timeMs > seg.end_time + 1000) continue;

               const wIdx = this.findWordIndexAtTime(seg, timeMs);
               if (wIdx !== -1) {
                   foundSegIndex = i;
                   foundWordIndex = wIdx;
                   break;
               }
           }
       }

       if (foundSegIndex !== -1) {
           this.applyHighlightDOM(foundSegIndex, foundWordIndex);
       } else {
           this.clearHighlightDOM();
       }
    },
    // 将二分查找抽取出来，增加复用性
    findWordIndexAtTime(segment, timeMs) {
        if (!segment.words || segment.words.length === 0) return -1;
        let left = 0;
        let right = segment.words.length - 1;
        while (left <= right) {
            const mid = Math.floor((left + right) / 2);
            const w = segment.words[mid];
            if (timeMs >= w.start_time && timeMs <= w.end_time) {
                return mid;
            } else if (timeMs < w.start_time) {
                right = mid - 1;
            } else {
                left = mid + 1;
            }
        }
        return -1;
    },
    applyHighlightDOM(segmentIndex, wordIndex) {
        if (!this.$el || typeof this.$el.querySelector !== 'function') return;
        if (this._highlightState && 
            this._highlightState.segmentIndex === segmentIndex && 
            this._highlightState.wordIndex === wordIndex) {
            return;
        }
        this.clearHighlightDOM();
        
        // 直接操作 DOM 添加高亮类，绕过 Vue 的响应式 diff
        const el = this.$el.querySelector(`span[data-word-id="${segmentIndex}-${wordIndex}"]`);
        if (el) {
            el.classList.add('playing');
        }
        this._highlightState = { segmentIndex, wordIndex };
    },
    clearHighlightDOM() {
        if (!this.$el || typeof this.$el.querySelector !== 'function') return;
        if (this._highlightState) {
            const { segmentIndex, wordIndex } = this._highlightState;
            const el = this.$el.querySelector(`span[data-word-id="${segmentIndex}-${wordIndex}"]`);
            if (el) {
                el.classList.remove('playing');
            }
            this._highlightState = null;
        }
    },
    getWordText(word) {
      if (word.text) return word.text;
      if (word.start_time != null && word.end_time != null) {
          const duration = (Number(word.end_time) - Number(word.start_time)) / 1000;
          // Avoid showing [空0.0秒] if duration is very small but not 0
          if (duration > 0) {
             return `[空${duration.toFixed(1)}秒]`;
          }
      }
      return '[空]';
    },
    formatTime(ms) {
      if (!ms && ms !== 0) return '00:00'
      const s = Math.floor(ms / 1000)
      const m = Math.floor(s / 60)
      const sec = s % 60
      return `${m}:${sec.toString().padStart(2, '0')}`
    },
    showVideoPreview(element, event) {
      if (!element.hot_video_url) return;
      this.cancelHidePreview();
      this.previewElement = element;
      
      const rect = event.target.getBoundingClientRect();
      const popupWidth = 240; // 预估 popup 宽度
      const popupHeight = 180; // 预估 popup 高度
      
      let left = rect.left - popupWidth - 10;
      if (left < 10) {
          left = rect.right + 10;
      }
      
      let top = rect.top;
      if (top + popupHeight > window.innerHeight) {
          top = window.innerHeight - popupHeight - 10;
      }
      
      this.previewStyle = {
          top: `${top}px`,
          left: `${left}px`
      };
    },
    hideVideoPreview() {
      this.hidePreviewTimer = setTimeout(() => {
          this.previewElement = null;
      }, 300); // 添加一点延迟，允许鼠标移动到 popup 上
    },
    cancelHidePreview() {
      if (this.hidePreviewTimer) {
          clearTimeout(this.hidePreviewTimer);
          this.hidePreviewTimer = null;
      }
    },
    onPreviewVideoLoaded() {
      if (this.$refs.previewVideo) {
        this.$refs.previewVideo.play().catch(e => {
           console.warn("Autoplay with sound failed, trying muted autoplay:", e);
           if (this.$refs.previewVideo) {
               this.$refs.previewVideo.muted = true;
               this.$refs.previewVideo.play().catch(err => {
                   console.error("Muted autoplay also failed:", err);
               });
           }
        });
      }
    }
  }
}
</script>

<style>
.video-preview-popup {
    position: fixed;
    z-index: 9999;
    background: #000;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    width: 240px;
    display: flex;
    flex-direction: column;
}

.preview-video {
    width: 100%;
    height: auto;
    max-height: 180px;
    background: #000;
}

.preview-info {
    padding: 8px;
    color: #fff;
    font-size: 12px;
    background: rgba(0, 0, 0, 0.8);
    text-align: left;
    white-space: normal;
    word-break: break-word;
    line-height: 1.4;
}
.draggable-group-container {
  width: 100%;
}
</style>

<style scoped>
/* Thin scrollbar styling */
.drag-wrapper::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.drag-wrapper::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}
.drag-wrapper::-webkit-scrollbar-track {
  background: transparent;
}
.drag-wrapper::-webkit-scrollbar-thumb:hover {
  background: #bbb;
}

.drag-wrapper {
  min-height: 60px; /* Allow dropping into empty list */
  max-height: 400px;
  overflow-y: auto;
  padding-bottom: 5px;
  border-radius: 4px;
}

.source-trace-wrapper {
  max-height: 250px !important;
}

.empty-group {
  background-color: #f9f9f9;
  border: 1px dashed #ccc;
  display: flex;
  justify-content: center;
  align-items: center;
}

.empty-placeholder {
  color: #aaa;
  font-size: 12px;
  pointer-events: none;
}

.segment-item {
  display: flex;
  flex-direction: column;
  padding: 8px;
  background: #f5f5f5;
  margin-bottom: 5px;
  border-radius: 4px;
  border: 1px solid #ddd;
  cursor: pointer; /* Add cursor pointer */
  transition: background-color 0.2s;
  
  /* 性能优化：告诉浏览器跳过屏幕外元素的渲染和布局计算 */
  content-visibility: auto;
  /* 预估高度，防止滚动条在渲染跳过时剧烈抖动 */
  contain-intrinsic-size: 80px;
}

.segment-item:hover {
    background-color: #e0e0e0;
}

.segment-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.drag-handle {
  cursor: grab;
  color: #888;
  margin-right: 8px;
  padding: 2px;
}

.drag-handle:active {
  cursor: grabbing;
}

.segment-info {
  flex: 1;
  overflow: hidden;
}

.seg-title {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.9em;
}

.seg-time {
  font-size: 0.8em;
  color: #666;
}

.segment-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 重复素材标记 */
.segment-item.dup-item {
  background-color: color-mix(in srgb, var(--dup-color, #f44336) 10%, #f5f5f5);
  border-left: 3px solid var(--dup-color, #f44336);
}
.segment-item.dup-item:hover {
  background-color: color-mix(in srgb, var(--dup-color, #f44336) 16%, #e0e0e0);
}
.dup-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 4px;
  color: #fff;
  font-size: 11px;
  font-weight: bold;
  cursor: pointer;
  margin-right: 2px;
  flex-shrink: 0;
}
.dup-badge:hover {
  filter: brightness(1.15);
}
/* 点击重复标记跳转时的短暂高亮 */
.segment-item.dup-jump-flash {
  box-shadow: 0 0 0 3px #ff9800;
  transition: box-shadow 0.2s;
}

.preview-btn-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.hot-video-btn {
  background: none;
  border: none;
  color: #ff9800;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hot-video-btn:hover {
  color: #f57c00;
}

.delete-btn {
  background: none;
  border: none;
  color: #999;
  font-size: 1.2em;
  cursor: pointer;
  padding: 0 5px;
}

.delete-btn:hover {
  color: #ff5252;
}

.fine-tuning-area {
    margin-top: 5px;
    padding-top: 5px;
    border-top: 1px dashed #ddd;
}

.no-words-hint {
    font-size: 0.8em;
    color: #999;
    font-style: italic;
}

.plain-text-content {
    font-size: 0.9em;
    color: #333;
    white-space: pre-wrap;
    line-height: 1.4;
    padding: 2px;
}

.words-container {
    display: block; /* 将原来的 flex 改为 block */
    user-select: none;
    line-height: 1.8;
}

.word-item {
    font-size: 0.85em;
    padding: 1px 2px;
    margin: 0;
    cursor: text;
    border-radius: 0;
    position: relative;
    /* 性能优化：强制浏览器为每个字创建独立的渲染层，隔离重绘区域 */
    will-change: background-color, color;
    contain: layout paint;
}

.split-btn-floating {
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: #2196f3;
    color: #fff;
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 14px;
    cursor: pointer;
    white-space: nowrap;
    z-index: 10;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    margin-bottom: 2px;
}

.split-btn-floating:hover {
    background: #1976d2;
}

.word-item:hover {
    background-color: #e3f2fd; /* Light blue hover */
}

.word-item.deleted {
    text-decoration: line-through;
    color: #999;
    background-color: #eee;
}

.word-item.playing {
    color: #ff5252;
    font-weight: bold;
    background-color: #fff3e0;
}

.word-item.selected {
    background-color: #2196f3;
    color: white;
}

.word-item.selected.deleted {
    color: rgba(255, 255, 255, 0.6);
}

.segment-item.unmatched {
    opacity: 0.6;
    background-color: #eee;
    cursor: not-allowed;
}

.segment-item.unmatched .seg-title {
    color: #666;
}

.selection-checkbox {
  margin-right: 8px;
  display: flex;
  align-items: center;
}

.selection-checkbox input[type="checkbox"] {
  cursor: pointer;
  width: 14px;
  height: 14px;
}
</style>
